import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './navigationmenu.css';
import { ArrowBack, Search } from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import { useFuseSearch } from '../../hooks/useFuseSearch';
import lottie from 'lottie-web';
import TextIconSrc from './../../assets/icons/navigationmenu/TextIcon.svg';
import LayerPanel from './layerpanel/LayerPanel.tsx';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
  generatePathPointsForCharacter,
  generatePathPointsForText,
  animateCharacterAlongPath,
  animateTextAlongPath,
} from '../canvas/utils/tweenAsset.ts';
import { stopRecording } from '../canvas/utils/recordAnimation.ts';
import {
  AssetType,
  CharacterRef,
  FontFamily,
  FontStyle,
  FontWeight,
  Position,
  TextRef,
  RiggedCharacterRef,
} from '../../types/typing.ts';
import { TextTemplate } from '../toolbar/ToolBar.tsx';
import { toast } from 'react-toastify';
import { toastMessage } from '../../i18n/toastMessages';
import {
  getEffectTypeFromImagePath,
  SUNLIGHT_EFFECT_PREVIEW,
  SUNLIGHT_EFFECT_TYPE,
} from '../canvas/utils/effectPreviews.ts';
import { getAssetDownloadUrl } from '../../services/assetUrlUtils';
import { GeneratedAudioResult } from '../../services/aiAudioService';
import {
  InsufficientPixlesError,
  NoActiveEntitlementError,
} from '../../services/entitlementErrors';
import {
  getMediaFileType,
  uploadAssetToVideo,
} from '../../services/uploadedAssetStorageService';
import {
  getUploadedAssets,
  saveUploadedAsset,
} from '../../services/uploadedAssetService';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import ImageIcon from '@mui/icons-material/Image';
import DataObjectIcon from '@mui/icons-material/DataObject';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FolderOffIcon from '@mui/icons-material/FolderOff';

/** Icon used in uploaded-asset previews. Replaces the old emoji glyphs with
 * theme-consistent Material icons. */
const AssetTypeIcon: React.FC<{ type: string }> = ({ type }) => {
  switch (type) {
    case 'audio':
      return <AudioFileIcon className="audio-icon" />;
    case 'video':
      return <VideoFileIcon className="video-icon" />;
    case 'image':
    case 'svg':
      return <ImageIcon className="image-icon" />;
    case 'json':
      return <DataObjectIcon className="json-icon" />;
    default:
      return <InsertDriveFileIcon className="unknown-icon" />;
  }
};

interface MenuProps {
  tweeningOptions: any[];
  selectedMenu: string;
  onClose: () => void;
  onAnimationSelect: (animationData: any) => void;
  onBackgroundSelect: (imageData: string | null) => void;
  onEffectSelect: (effect: any) => void;
  onUploadedAssetSelect: (
    assetUrl: string,
    assetType?: string,
    fileName?: string
  ) => void;
  onTextSelect: (template?: TextTemplate) => void;
  onAudioSelect: (audio: any, fileName?: string) => void;
  layers: any;
  charactersRef: React.MutableRefObject<Map<string, CharacterRef>>;
  textRef: React.MutableRefObject<Map<string, TextRef>>;
  onLayerDrag(draggedIndex: number, droppedIndex: number): void;
  handleLayerLock: (index: number) => void;
  onUpdateCharacter: (
    id: string,
    properties: {
      position?: { x: number; y: number };
      scale?: number;
      pathPoints?: Position[];
    }
  ) => void;
  onUpdateText: (id: string, textProps: Partial<TextRef>) => void;
  onUpdateIsRecording: (isRecording: boolean) => void;
  onUpdateRecordingStartTime: (startTime: number | null) => void;
  isRecording: boolean;
  selectedAssetOnCanvas: { id: string; selectedAsset: AssetType };
  navMenuRef: React.RefObject<HTMLDivElement>;
  onAiAudioRequest: (data: any) => Promise<GeneratedAudioResult>;
  onAiBackgroundRequest: (data: any) => Promise<string>;
  onSaveAndCanvasImportBackground: (imageUrl: string) => void;
  onSaveAndCanvasImportAudio: (
    audioUrl: string,
    title?: string,
    duration?: number
  ) => void;
  lastSelectedCharacterIndex: string | null;
  videoId?: string;
  videoSas?: {
    token?: string;
    expiredAt?: number;
  };
  audioFiles: any[];
  setAudioFiles: (audioFiles: any[]) => void;
  riggedCharactersRef: React.MutableRefObject<Map<string, RiggedCharacterRef>>;
  onUpdateRiggedCharacter?: (
    id: string,
    properties: {
      position?: { x: number; y: number };
      scale?: number;
      pathPoints?: Position[];
    }
  ) => void;
}

/** Credit-gate kind for the AI audio/background upsell banners. */
type CreditGateKind = 'insufficient' | 'entitlement';

const resolveCreditGate = (error: unknown): CreditGateKind | null => {
  if (error instanceof NoActiveEntitlementError) return 'entitlement';
  if (error instanceof InsufficientPixlesError) return 'insufficient';
  return null;
};

/**
 * Credit-gate upsell banner for the AI audio/background panels. Checkout
 * lives ONLY on /billing — this is status + routing, like the generator
 * banners in CharacterVideoGenerator.
 */
const CreditGateBanner: React.FC<{
  gate: CreditGateKind;
  resource: string;
  onDismiss: () => void;
}> = ({ gate, resource, onDismiss }) => {
  const navigate = useNavigate();
  const isEntitlement = gate === 'entitlement';
  return (
    <div className="ai-credits-banner" role="alert">
      <span>
        {isEntitlement
          ? `An active subscription is required for this ${resource}.`
          : `Not enough credits for this ${resource}.`}
      </span>
      <div className="ai-credits-banner-actions">
        <button
          type="button"
          className="generate-audio-button"
          onClick={() => navigate('/billing')}
        >
          {isEntitlement ? 'Subscribe to Pro' : 'Top up credits'}
        </button>
        <button
          type="button"
          className="ai-credits-banner-dismiss"
          onClick={onDismiss}
          aria-label="Dismiss"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
};

const Menu: React.FC<MenuProps> = ({
  tweeningOptions,
  selectedMenu,
  onClose,
  onAnimationSelect,
  onBackgroundSelect,
  onEffectSelect,
  onUploadedAssetSelect,
  onTextSelect,
  onAudioSelect,
  layers,
  onLayerDrag,
  handleLayerLock,
  onUpdateCharacter,
  onUpdateText,
  charactersRef,
  textRef,
  onUpdateIsRecording,
  onUpdateRecordingStartTime,
  isRecording,
  selectedAssetOnCanvas,
  navMenuRef,
  onAiAudioRequest,
  onAiBackgroundRequest,
  onSaveAndCanvasImportBackground,
  onSaveAndCanvasImportAudio,
  lastSelectedCharacterIndex,
  videoId,
  videoSas,
  audioFiles,
  setAudioFiles,
  riggedCharactersRef,
  onUpdateRiggedCharacter,
}) => {
  const [lottieFiles, setLottieFiles] = useState<any[]>([]);
  const [lottiePropFiles, setLottiePropFiles] = useState<any[]>([]);
  const [backgroundImages, setBackgroundImages] = useState<string[]>([]);
  const [effectImages, setEffectImages] = useState<string[]>([]);
  const [uploadedAssets, setUploadedAssets] = useState<string[]>([]);
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const uploadDragDepthRef = useRef(0);
  const [uploadedAssetsByCategory, setUploadedAssetsByCategory] = useState<{
    [key: string]: { url: string; type: string; filename: string }[];
  }>({
    characters: [],
    props: [],
    backgrounds: [],
    effects: [],
    audio: [],
    assets: [],
  });
  const [isLoadingAssets, setIsLoadingAssets] = useState<boolean>(false);
  const [assetTypes, setAssetTypes] = useState<{
    [key: string]: { type: string; filename: string };
  }>({});
  const lottieRefs = useRef<(HTMLDivElement | null)[]>([]);
  const lottiePropRefs = useRef<(HTMLDivElement | null)[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [lottieFileNames, setLottieFileNames] = useState<string[]>([]);
  const [lottiePropFileNames, setLottiePropFileNames] = useState<string[]>([]);
  const [isAiSelected, setIsAiSelected] = useState<boolean>(false);
  const [isUploadedSelected, setIsUploadedSelected] = useState<boolean>(false);
  const [audioPrompt, setAudioPrompt] = useState<string>('');
  const [audioPromptTitle, setAudioPromptTitle] = useState<string>('');
  const [audioPromptStyle, setAudioPromptStyle] = useState<string>('');
  const [backgroundPrompt, setBackgroundPrompt] = useState<string>('');
  const [generatedBackgroundImage, setGeneratedBackgroundImage] =
    useState<string>('');
  const [generatedAudioUrl, setGeneratedAudioUrl] = useState<string>('');
  const [generatedAudioSourceUrl, setGeneratedAudioSourceUrl] =
    useState<string>('');
  const [generatedAudioDuration, setGeneratedAudioDuration] = useState<
    number | undefined
  >(undefined);
  // Credit-gate banners for the AI panels (402 → top-up, 403 → subscribe).
  const [audioGate, setAudioGate] = useState<CreditGateKind | null>(null);
  const [backgroundGate, setBackgroundGate] = useState<CreditGateKind | null>(
    null
  );
  const effectMenuItems = useMemo(() => {
    const menuItems = effectImages.map((image) => ({
      image,
      type: getEffectTypeFromImagePath(image),
    }));

    if (!menuItems.some((item) => item.type === SUNLIGHT_EFFECT_TYPE)) {
      menuItems.push({
        image: SUNLIGHT_EFFECT_PREVIEW,
        type: SUNLIGHT_EFFECT_TYPE,
      });
    }

    return menuItems;
  }, [effectImages]);

  const textTemplates: TextTemplate[] = [
    {
      label: 'Title Pop',
      text: 'Amazing Adventure',
      fontProperties: {
        fontFamily: FontFamily.Impact,
        fontSize: 54,
        fontColor: '#FF3D6E',
        fontStyle: FontStyle.Normal,
        fontWeight: FontWeight.Bold,
      },
    },
    {
      label: 'Learning Label',
      text: 'Today We Learn!',
      fontProperties: {
        fontFamily: FontFamily.Verdana,
        fontSize: 42,
        fontColor: '#2A9D8F',
        fontStyle: FontStyle.Normal,
        fontWeight: FontWeight.Bold,
      },
    },
    {
      label: 'Story Bubble',
      text: 'Once upon a time...',
      styleType: 'bubble',
      fontProperties: {
        fontFamily: FontFamily.Georgia,
        fontSize: 40,
        fontColor: '#6A58A5',
        fontStyle: FontStyle.Italic,
        fontWeight: FontWeight.Bold,
      },
    },
    {
      label: 'Hero Name',
      text: 'Captain Spark',
      fontProperties: {
        fontFamily: FontFamily.TrebuchetMS,
        fontSize: 48,
        fontColor: '#F77F00',
        fontStyle: FontStyle.Normal,
        fontWeight: FontWeight.Bold,
      },
    },
    {
      label: 'Quiz Prompt',
      text: 'Can you guess?',
      fontProperties: {
        fontFamily: FontFamily.Helvetica,
        fontSize: 44,
        fontColor: '#118AB2',
        fontStyle: FontStyle.Normal,
        fontWeight: FontWeight.Bold,
      },
    },
    {
      label: 'Comic Wow',
      text: 'WOW!',
      fontProperties: {
        fontFamily: FontFamily.Impact,
        fontSize: 64,
        fontColor: '#FFD166',
        fontStyle: FontStyle.Normal,
        fontWeight: FontWeight.Bold,
      },
    },
  ];

  // ── Fuse.js fuzzy search (local, ~50 entries per menu) ──
  const prettyFileLabel = (src: string, fallback: string) => {
    try {
      const base = decodeURIComponent(
        src.split('/').pop()?.split('?')[0]?.split('.')[0] || ''
      )
        .replace(/[-_]+/g, ' ')
        .trim();
      return base || fallback;
    } catch {
      return fallback;
    }
  };

  const characterSearchItems = useMemo(
    () =>
      lottieFiles.map((_, index) => ({
        name: lottieFileNames[index] || `Character ${index + 1}`,
        originalIndex: index,
      })),
    [lottieFiles, lottieFileNames]
  );
  const filteredCharacters = useFuseSearch(
    characterSearchItems,
    ['name'],
    searchTerm
  );

  const propSearchItems = useMemo(
    () =>
      lottiePropFiles.map((_, index) => ({
        name: lottiePropFileNames[index] || `Prop ${index + 1}`,
        originalIndex: index,
      })),
    [lottiePropFiles, lottiePropFileNames]
  );
  const filteredProps = useFuseSearch(propSearchItems, ['name'], searchTerm);

  const audioSearchItems = useMemo(
    () =>
      (audioFiles || []).map((src: string, index: number) => ({
        src,
        label: prettyFileLabel(src, `Audio ${index + 1}`),
        originalIndex: index,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [audioFiles]
  );
  const filteredAudio = useFuseSearch(
    audioSearchItems,
    [{ name: 'label', weight: 2 }, 'src'],
    searchTerm
  );

  const filteredUploadedAudio = useFuseSearch(
    uploadedAssetsByCategory.audio || [],
    ['filename', 'url'],
    searchTerm
  );
  const filteredUploadedAssets = useFuseSearch(
    uploadedAssetsByCategory.assets || [],
    ['filename', 'url'],
    searchTerm
  );
  const filteredUploadedCharacters = useFuseSearch(
    uploadedAssetsByCategory.characters || [],
    ['filename', 'url'],
    searchTerm
  );
  const filteredUploadedProps = useFuseSearch(
    uploadedAssetsByCategory.props || [],
    ['filename', 'url'],
    searchTerm
  );
  const filteredUploadedBackgrounds = useFuseSearch(
    uploadedAssetsByCategory.backgrounds || [],
    ['filename', 'url'],
    searchTerm
  );
  const filteredUploadedEffects = useFuseSearch(
    uploadedAssetsByCategory.effects || [],
    ['filename', 'url'],
    searchTerm
  );

  const assetsSearchItems = useMemo(
    () =>
      (uploadedAssets || []).map((url: string, index: number) => ({
        url,
        label:
          assetTypes[url]?.filename ||
          prettyFileLabel(url, `Asset ${index + 1}`),
        type: assetTypes[url]?.type || 'unknown',
        originalIndex: index,
      })),
    [uploadedAssets, assetTypes]
  );
  const filteredAssets = useFuseSearch(
    assetsSearchItems,
    [{ name: 'label', weight: 2 }, 'url'],
    searchTerm
  );

  const backgroundSearchItems = useMemo(
    () =>
      backgroundImages.map((src, index) => ({
        src,
        label: `Background ${index + 1} ${prettyFileLabel(src, '')}`.trim(),
        originalIndex: index,
      })),
    [backgroundImages]
  );
  const filteredBackgrounds = useFuseSearch(
    backgroundSearchItems,
    ['label', 'src'],
    searchTerm
  );

  const filteredEffects = useFuseSearch(
    effectMenuItems,
    [{ name: 'type', weight: 2 }, 'image'],
    searchTerm
  );

  const filteredTextTemplates = useFuseSearch(
    textTemplates,
    [{ name: 'label', weight: 2 }, 'text'],
    searchTerm
  );

  const templateTweeningOptions = useMemo(
    () =>
      (tweeningOptions || []).filter(
        (option: any) => option.type !== 'record' && option.type !== 'playback'
      ),
    [tweeningOptions]
  );
  const filteredTweeningOptions = useFuseSearch(
    templateTweeningOptions,
    ['text', 'label', 'type'],
    searchTerm
  );

  const layerSearchItems = useMemo(
    () =>
      (layers || []).map((layer: any, index: number) => ({
        ...layer,
        originalIndex: index,
      })),
    [layers]
  );
  const filteredLayers = useFuseSearch(
    layerSearchItems,
    [{ name: 'name', weight: 2 }, 'type'],
    searchTerm
  );

  useEffect(() => {
    setSearchTerm('');
    setIsAiSelected(false);
    setIsUploadedSelected(false);
  }, [selectedMenu]);

  useEffect(() => {
    if (selectedMenu === 'characters') {
      const importLottieFiles = async () => {
        const modules = import.meta.glob(
          '../../assets/animations/characters/*.json'
        );
        const paths = Object.keys(modules);
        const lottieModules = await Promise.all(
          Object.values(modules).map((module) => module())
        );
        setLottieFiles(lottieModules);
        setLottieFileNames(
          paths.map(
            (p) =>
              p
                .split('/')
                .pop()
                ?.replace(/\.json$/i, '')
                .replace(/[-_]+/g, ' ') || ''
          )
        );
      };
      importLottieFiles();
    }

    if (selectedMenu === 'props') {
      const importLottiePropFiles = async () => {
        const modules = import.meta.glob(
          '../../assets/animations/props/*.json'
        );
        const paths = Object.keys(modules);
        const lottiePropModules = await Promise.all(
          Object.values(modules).map((module) => module())
        );
        setLottiePropFiles(lottiePropModules);
        setLottiePropFileNames(
          paths.map(
            (p) =>
              p
                .split('/')
                .pop()
                ?.replace(/\.json$/i, '')
                .replace(/[-_]+/g, ' ') || ''
          )
        );
      };
      importLottiePropFiles();
    }

    if (selectedMenu === 'audio') {
      const importAudioFiles = async () => {
        try {
          // Import local audio files
          const modules = import.meta.glob('../../assets/audio/*.{mpeg,mp3}');
          const audioPaths = await Promise.all(
            Object.keys(modules).map(async (path) => {
              try {
                const module: any = await modules[path]();
                return module.default;
              } catch (error) {
                console.error('Error loading audio file:', path, error);
                return null;
              }
            })
          ).then((paths) => paths.filter((path) => path !== null));

          // Load and validate saved dynamic audio
          let savedDynamicAudio: string[] = [];
          try {
            const savedAudio = localStorage.getItem('dynamicAudio');
            if (savedAudio) {
              savedDynamicAudio = JSON.parse(savedAudio);
              // Validate URLs
              savedDynamicAudio = savedDynamicAudio.filter((url) => {
                try {
                  new URL(url);
                  return true;
                } catch {
                  console.error('Invalid audio URL:', url);
                  return false;
                }
              });
            }
          } catch (error) {
            console.error('Error loading saved audio:', error);
            // Clear invalid data
            localStorage.removeItem('dynamicAudio');
          }

          // Combine and set audio files
          if (savedDynamicAudio.length > 0) {
            setAudioFiles([...audioPaths, ...savedDynamicAudio]);
          } else {
            setAudioFiles(audioPaths);
          }
        } catch (error) {
          console.error('Error importing audio files:', error);
          setAudioFiles([]);
        }
      };
      importAudioFiles();
    }

    if (selectedMenu === 'backgrounds') {
      const importBackgroundImages = async () => {
        const modules = import.meta.glob('../../assets/backgrounds/*.png');
        const imagePaths = await Promise.all(
          Object.keys(modules).map(async (path) => {
            const module: any = await modules[path]();
            return module.default;
          })
        );
        setBackgroundImages(imagePaths);
      };
      importBackgroundImages();
    }

    if (selectedMenu === 'effects') {
      const importEffectListingImages = async () => {
        const modules = import.meta.glob('../../assets/effects/listing/*.png');
        const effectPaths = await Promise.all(
          Object.keys(modules).map(async (path) => {
            const module: any = await modules[path]();
            return module.default;
          })
        );
        setEffectImages(effectPaths);
      };
      importEffectListingImages();
      //loading it here since otherwise it is not packaged into js build
      //To be removed after switching to blob storage
      const importEffectsImages = async () => {
        const modules = import.meta.glob('../../assets/effects/*.png');
        await Promise.all(
          Object.keys(modules).map(async (path) => {
            const module: any = await modules[path]();
            return module.default;
          })
        );
      };
      importEffectsImages();
    }
  }, [selectedMenu, isAiSelected]);

  useEffect(() => {
    // Clean up previous animations
    lottieRefs.current.forEach((ref) => {
      if (ref) {
        while (ref.firstChild) {
          ref.removeChild(ref.firstChild);
        }
      }
    });

    // Load new animations (refs are keyed by originalIndex so filtering is safe)
    lottieRefs.current.forEach((ref, index) => {
      if (ref && lottieFiles[index]) {
        lottie.loadAnimation({
          container: ref,
          renderer: 'svg',
          loop: true,
          autoplay: true,
          animationData: lottieFiles[index].default || lottieFiles[index],
        });
      }
    });
  }, [lottieFiles, filteredCharacters]);

  useEffect(() => {
    // Clean up previous animations
    lottiePropRefs.current.forEach((ref) => {
      if (ref) {
        while (ref.firstChild) {
          ref.removeChild(ref.firstChild);
        }
      }
    });

    // Load new animations (refs are keyed by originalIndex so filtering is safe)
    lottiePropRefs.current.forEach((ref, index) => {
      if (ref && lottiePropFiles[index]) {
        lottie.loadAnimation({
          container: ref,
          renderer: 'svg',
          loop: true,
          autoplay: true,
          animationData:
            lottiePropFiles[index].default || lottiePropFiles[index],
        });
      }
    });
  }, [lottiePropFiles, filteredProps]);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const handleAnimationClick = (index: number) => {
    const animationData =
      selectedMenu === 'characters'
        ? lottieFiles[index]?.default || lottieFiles[index]
        : lottiePropFiles[index]?.default || lottiePropFiles[index];

    onAnimationSelect(animationData);
  };

  const getSelectedAssetRef = () => {
    if (selectedAssetOnCanvas.selectedAsset === AssetType.CHARACTER) {
      return charactersRef;
    }
    if (selectedAssetOnCanvas.selectedAsset === AssetType.TEXT) {
      return textRef;
    }
    if (selectedAssetOnCanvas.selectedAsset === AssetType.RIGGED_CHARACTER) {
      return riggedCharactersRef;
    }
    return null;
  };

  const getSelectedAsset = () => {
    const assetRef = getSelectedAssetRef();
    if (!assetRef) return null;

    const asset =
      selectedAssetOnCanvas.selectedAsset === AssetType.CHARACTER
        ? Array.from(charactersRef.current.values()).find(
            (char: CharacterRef) => char.isSelected
          )
        : selectedAssetOnCanvas.selectedAsset === AssetType.TEXT
          ? Array.from(textRef.current.values()).find(
              (text: TextRef) => text.isSelected
            )
          : Array.from(riggedCharactersRef.current.values()).find(
              (char: RiggedCharacterRef) => char.isSelected
            );
    return asset;
  };

  const handleRecording = () => {
    if (!isRecording) {
      onUpdateIsRecording(true);
      onUpdateRecordingStartTime(Date.now());
    } else {
      onUpdateIsRecording(false);
      onUpdateRecordingStartTime(Date.now());
      const assetRef = getSelectedAssetRef();
      if (!assetRef) return;

      // Use the appropriate update function based on asset type
      if (selectedAssetOnCanvas.selectedAsset === AssetType.RIGGED_CHARACTER) {
        if (onUpdateRiggedCharacter) {
          const movements = stopRecording(
            assetRef as any,
            onUpdateRiggedCharacter
          );
          console.log('Rigged character movements:', movements);
        }
      } else {
        const movements = stopRecording(assetRef as any, onUpdateCharacter);
        console.log('Character movements:', movements);
      }
    }
  };

  const handlePlayback = () => {
    const assetRef = getSelectedAssetRef();
    if (!assetRef) return;

    const selectedAsset = getSelectedAsset();
    console.log('selectedAsset', selectedAsset);
    if (!selectedAsset?.pathPoints?.length) {
      toast.error(toastMessage('navigation.noRecordedAnimation'));
      return;
    }

    if (selectedAssetOnCanvas.selectedAsset === AssetType.CHARACTER) {
      const selectedCharacter = Array.from(charactersRef.current.values()).find(
        (character) => character.id === lastSelectedCharacterIndex
      );

      if (!selectedCharacter) return;
      const animation = animateCharacterAlongPath(
        selectedCharacter,
        onUpdateCharacter
      );
      animation?.play();
    } else if (
      selectedAssetOnCanvas.selectedAsset === AssetType.RIGGED_CHARACTER
    ) {
      const selectedCharacter = Array.from(
        riggedCharactersRef.current.values()
      ).find((character) => character.isSelected);

      if (!selectedCharacter) return;
      if (!onUpdateRiggedCharacter) {
        toast.error(toastMessage('navigation.characterUpdateUnavailable'));
        return;
      }
      const animation = animateCharacterAlongPath(
        selectedCharacter,
        onUpdateRiggedCharacter
      );
      animation?.play();
    } else if (selectedAssetOnCanvas.selectedAsset === AssetType.TEXT) {
      const selectedText = Array.from(textRef.current.values()).find(
        (text) => text.isSelected
      );

      if (!selectedText) return;
      const animation = animateTextAlongPath(selectedText, onUpdateText);
      animation?.play();
    }
  };

  const handleTemplateTweening = (option: any) => {
    const assetRef = getSelectedAssetRef();
    if (!assetRef) return;

    // Clear existing path circles and path points
    if (
      selectedAssetOnCanvas.selectedAsset === AssetType.CHARACTER &&
      lastSelectedCharacterIndex
    ) {
      const selectedCharacter = charactersRef.current.get(
        lastSelectedCharacterIndex
      );
      if (selectedCharacter) {
        // Clear path circles
        if (selectedCharacter.pathCircles) {
          selectedCharacter.pathCircles.forEach((circle: any) => {
            if (circle.parent) {
              circle.parent.removeChild(circle);
            }
          });
          selectedCharacter.pathCircles = [];
        }
        // Clear path points
        selectedCharacter.pathPoints = [];
      }
      generatePathPointsForCharacter(
        assetRef as React.MutableRefObject<
          Map<string, CharacterRef | RiggedCharacterRef>
        >,
        lastSelectedCharacterIndex || (getSelectedAsset()?.id as string),
        onUpdateCharacter,
        option
      );
    } else if (
      selectedAssetOnCanvas.selectedAsset === AssetType.RIGGED_CHARACTER
    ) {
      // Handle rigged characters
      const selectedRiggedCharacter = Array.from(
        riggedCharactersRef.current.values()
      ).find((character: RiggedCharacterRef) => character.isSelected);
      if (selectedRiggedCharacter) {
        // Clear path circles
        if (selectedRiggedCharacter.pathCircles) {
          selectedRiggedCharacter.pathCircles.forEach((circle: any) => {
            if (circle.parent) {
              circle.parent.removeChild(circle);
            }
          });
          selectedRiggedCharacter.pathCircles = [];
        }
        // Clear path points
        selectedRiggedCharacter.pathPoints = [];
      }
      if (!onUpdateRiggedCharacter) {
        toast.error(toastMessage('navigation.characterUpdateUnavailable'));
        return;
      }
      generatePathPointsForCharacter(
        riggedCharactersRef as React.MutableRefObject<
          Map<string, CharacterRef | RiggedCharacterRef>
        >,
        selectedRiggedCharacter?.id || (getSelectedAsset()?.id as string),
        onUpdateRiggedCharacter,
        option
      );
    } else if (selectedAssetOnCanvas.selectedAsset === AssetType.TEXT) {
      const selectedText = Array.from(textRef.current.values()).find(
        (text: TextRef) => text.isSelected
      );
      if (selectedText) {
        // Clear path circles
        if (selectedText.pathCircles) {
          selectedText.pathCircles.forEach((circle: any) => {
            if (circle.parent) {
              circle.parent.removeChild(circle);
            }
          });
          selectedText.pathCircles = [];
        }
        // Clear path points
        selectedText.pathPoints = [];
      }
      generatePathPointsForText(
        assetRef as React.MutableRefObject<Map<string, TextRef>>,
        option,
        onUpdateText
      );
    }
  };

  const handleTweeningOptionClick = (option: any) => {
    switch (option.type) {
      case 'record':
        handleRecording();
        break;
      case 'playback':
        handlePlayback();
        break;
      default:
        handleTemplateTweening(option);
    }
  };

  const handleEffectMenuItemClick = (effectType: string) => {
    if (!effectType) return;

    onEffectSelect(effectType);
  };

  const handleBackgroundClick = (index: number) => {
    const backgroundImageUrl = backgroundImages[index];
    onBackgroundSelect(backgroundImageUrl);
  };

  const handleAudioPlay = (src: string) => {
    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    // Create and play new audio. Stored audio may be a relative blob path
    // (e.g. `videos/<id>/assets/foo.mp3`), so resolve it with the current
    // video's SAS token before loading; absolute/data URLs pass through.
    const playableSrc = getAssetDownloadUrl(src, videoSas?.token);
    const audio = new Audio(playableSrc);
    audioRef.current = audio; // Assign ref before playing
    audio.addEventListener('ended', () => {
      audioRef.current = null;
    });

    audio
      .play()
      .then(() => {
        audioRef.current = audio;
      })
      .catch((error: any) => {
        // A play() interrupted by a newer play()/pause() — e.g. the user clicks
        // another track (or the same one) while this one is still loading —
        // throws an AbortError. That is expected and harmless, so ignore it.
        if (error?.name === 'AbortError') return;
        console.error('Error playing audio:', error);
      });
  };

  const handleAudioStop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
  };

  // Load the uploaded assets saved against the current video id and surface
  // them in the Assets/Audio menus below the uploader.
  useEffect(() => {
    if (!videoId) return;

    const token = localStorage.getItem('authToken');
    if (!token) return;

    let cancelled = false;

    getUploadedAssets(token)
      .then((assets) => {
        if (cancelled) return;

        const nextUploadedAssets: string[] = [];
        const nextAssetTypes: Record<
          string,
          { type: string; filename: string }
        > = {};
        const nextByCategory: {
          [key: string]: { url: string; type: string; filename: string }[];
        } = {
          characters: [],
          props: [],
          backgrounds: [],
          effects: [],
          audio: [],
          assets: [],
        };

        assets.forEach((asset) => {
          const filename = asset.location.split('/').pop() || asset.location;
          const category = asset.type === 'audio' ? 'audio' : 'assets';

          nextUploadedAssets.push(asset.location);
          nextAssetTypes[asset.location] = { type: asset.type, filename };
          nextByCategory[category].push({
            url: asset.location,
            type: asset.type,
            filename,
          });
        });

        setUploadedAssets(nextUploadedAssets);
        setAssetTypes(nextAssetTypes);
        setUploadedAssetsByCategory((prev) => ({
          ...prev,
          audio: nextByCategory.audio,
          assets: nextByCategory.assets,
        }));
      })
      .catch((error) =>
        console.error('[UploadAsset] Failed to load uploaded assets:', error)
      );

    return () => {
      cancelled = true;
    };
  }, [videoId]);

  const handleUploadDragEnter = () => {
    uploadDragDepthRef.current += 1;
    setIsDragOver(true);
  };

  const handleUploadDragLeave = () => {
    uploadDragDepthRef.current -= 1;
    if (uploadDragDepthRef.current <= 0) {
      uploadDragDepthRef.current = 0;
      setIsDragOver(false);
    }
  };

  const handleUploadDragOver = (event: React.DragEvent<HTMLInputElement>) => {
    event.preventDefault();
  };

  const handleUploadDrop = () => {
    uploadDragDepthRef.current = 0;
    setIsDragOver(false);
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    // IMPORTANT: Snapshot the selected files into a plain array FIRST. The
    // browser's FileList is live and tied to the input — resetting the input
    // value below would otherwise clear the list before we read it, causing
    // every real upload to be rejected with an empty file list.
    const selectedFiles = Array.from(event.target.files || []);
    // Reset the input so re-selecting the same file re-triggers change events.
    event.target.value = '';

    // Version marker so we can confirm which build is running in the browser.
    console.log(
      '[UploadAsset] handler v2 — files snapshotted BEFORE reset:',
      selectedFiles.map((f) => f.name)
    );

    if (selectedFiles.length === 0) return;

    // ── DEBUG LOGS ──────────────────────────────────────────────────────────
    // Log every file the input produced so we can see what the browser reports
    // for an MP4 (name, MIME type, extension) and why it gets rejected.
    console.log('[UploadAsset] selected files:', {
      count: selectedFiles.length,
      files: selectedFiles.map((file) => ({
        name: file.name,
        extension: file.name.split('.').pop()?.toLowerCase() || '',
        type: file.type || '(empty MIME type)',
        size: file.size,
      })),
      mimeSupported: selectedFiles.map((file) => ({
        name: file.name,
        mimeStartsWithAudio: file.type.split('/')[0] === 'audio',
        mimeStartsWithVideo: file.type.split('/')[0] === 'video',
      })),
    });
    // ── END DEBUG LOGS ──────────────────────────────────────────────────────

    // Only audio and video asset uploads are allowed. Detection falls back to
    // the file extension because browsers often return an empty MIME type for
    // video (and some audio) files.
    const mediaFiles = selectedFiles
      .map((file) => ({ file, fileType: getMediaFileType(file) }))
      .filter(
        (entry): entry is { file: File; fileType: 'audio' | 'video' } =>
          entry.fileType !== null
      );

    console.log('[UploadAsset] getMediaFileType result per file:', {
      results: selectedFiles.map((file) => ({
        name: file.name,
        fileType: getMediaFileType(file),
      })),
      accepted: mediaFiles.map(({ file, fileType }) => ({
        name: file.name,
        type: file.type || '(empty MIME type)',
        fileType,
      })),
      rejected: selectedFiles.filter((file) => getMediaFileType(file) === null),
    });

    if (mediaFiles.length === 0) {
      console.warn('[UploadAsset] No audio/video files accepted — rejecting', {
        filesProvided: selectedFiles.map((file) => ({
          name: file.name,
          type: file.type || '(empty MIME type)',
          extension: file.name.split('.').pop()?.toLowerCase() || '',
        })),
      });
      toast.error(toastMessage('navigation.assetsOnlyAudioVideo'));
      return;
    }

    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error(toastMessage('common.authTokenMissing'));
      return;
    }

    if (!videoId) {
      toast.error(toastMessage('common.videoIdMissing'));
      return;
    }

    setIsLoadingAssets(true);

    try {
      // Upload every file against the video id. getUploadURI returns a signed
      // upload URL plus the relative blob location (stored under the video's
      // storage folder), which we keep in frontend state and resolve with the
      // video's SAS token when the asset needs to play back.
      await Promise.all(
        mediaFiles.map(async ({ file, fileType }) => {
          console.log('[UploadAsset] uploading file against video:', {
            videoId,
            fileName: file.name,
            type: file.type || '(empty MIME type)',
            fileType,
          });
          const location = await uploadAssetToVideo(file, videoId, token);
          console.log('[UploadAsset] upload completed, location:', {
            fileName: file.name,
            location,
          });

          // Persist the stored location against the video id so it is saved on
          // the backend and survives reloads.
          try {
            await saveUploadedAsset(
              { location, type: fileType, videoId },
              token
            );
            console.log('[UploadAsset] asset persisted against video:', {
              location,
              type: fileType,
              videoId,
            });
          } catch (persistError) {
            // The blob is stored and available locally; log but do not fail the
            // whole flow if the persistence call fails.
            console.error(
              '[UploadAsset] Failed to persist uploaded asset:',
              persistError
            );
          }

          setUploadedAssets((prevAssets) => [...prevAssets, location]);

          // Set the asset type and filename for this location
          setAssetTypes((prev) => ({
            ...prev,
            [location]: {
              type: fileType,
              filename: file.name,
            },
          }));

          // Audio assets are surfaced under the Audio menu; video assets under
          // the Assets menu.
          const category = fileType === 'audio' ? 'audio' : 'assets';

          // Add to the appropriate category
          setUploadedAssetsByCategory((prev) => ({
            ...prev,
            [category]: [
              ...prev[category],
              { url: location, type: fileType, filename: file.name },
            ],
          }));
        })
      );
    } catch (error) {
      console.error('Failed to upload asset:', error);
      toast.error(toastMessage('navigation.assetsUploadFailed'));
    } finally {
      setIsLoadingAssets(false);
    }
  };

  const handleAssetClick = (
    url: string,
    assetType?: string,
    fileName?: string
  ) => {
    onUploadedAssetSelect(url, assetType, fileName);
  };

  const handleGenerateAudioClick = async () => {
    if (audioPrompt === '') {
      toast.error(toastMessage('navigation.audioPromptRequired'));
      return;
    }
    if (audioPromptTitle === '') {
      toast.error(toastMessage('navigation.audioTitleRequired'));
      return;
    }
    if (audioPromptStyle === '') {
      toast.error(toastMessage('navigation.audioStyleRequired'));
      return;
    }
    // Fresh attempt: clear any prior credit-gate banner.
    setAudioGate(null);
    try {
      const result = await onAiAudioRequest({
        prompt: audioPrompt,
        title: audioPromptTitle,
        style: audioPromptStyle,
      });
      setGeneratedAudioUrl(result?.audioUrl || '');
      setGeneratedAudioSourceUrl(result?.sourceAudioUrl || '');
      setGeneratedAudioDuration(result?.duration);
    } catch (error: any) {
      const gate = resolveCreditGate(error);
      if (gate) {
        setAudioGate(gate);
      } else {
        toast.error(
        error?.message || toastMessage('navigation.audioGenerateFailed')
      );
      }
    }
  };
  const handleGenerateBackgroundClick = async () => {
    if (backgroundPrompt === '') {
      toast.error(toastMessage('navigation.backgroundPromptRequired'));
      return;
    }
    // Fresh attempt: clear any prior credit-gate banner.
    setBackgroundGate(null);
    try {
      const bgImageUrl = await onAiBackgroundRequest({
        prompt: backgroundPrompt,
      });
      setGeneratedBackgroundImage(bgImageUrl);
    } catch (error: any) {
      const gate = resolveCreditGate(error);
      if (gate) {
        setBackgroundGate(gate);
      } else {
        toast.error(
        error?.message || toastMessage('navigation.backgroundGenerateFailed')
      );
      }
    }
  };

  return (
    <div ref={navMenuRef} className="menu-content">
      <div className="menu-header">
        <div className="search-container">
          <Search className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder={`Search ${selectedMenu.charAt(0).toUpperCase() + selectedMenu.slice(1)}`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <IconButton onClick={onClose} className="back-button">
          <ArrowBack />
        </IconButton>
      </div>
      <div className="categories-header">
        <div className="category-tabs">
          <div
            className={`category-tab ${!isAiSelected && !isUploadedSelected ? 'active' : ''}`}
            onClick={() => {
              setIsAiSelected(false);
              setIsUploadedSelected(false);
            }}
          >
            {selectedMenu.charAt(0).toUpperCase() + selectedMenu.slice(1)}
          </div>
          {!['text', 'assets', 'layer', 'animations'].includes(
            selectedMenu
          ) && (
            <>
              <div
                className={`category-tab ${isAiSelected ? 'active' : ''}`}
                onClick={() => {
                  setIsAiSelected(true);
                  setIsUploadedSelected(false);
                }}
              >
                Use AI
              </div>
              <div
                className={`category-tab ${isUploadedSelected ? 'active' : ''}`}
                onClick={() => {
                  setIsAiSelected(false);
                  setIsUploadedSelected(true);
                }}
              >
                Uploaded
              </div>
            </>
          )}
        </div>
      </div>
      <div className="scrollable-content">
        {selectedMenu === 'characters' &&
          !isAiSelected &&
          !isUploadedSelected && (
            <div className="lottie-container" key="characters-templated">
              {filteredCharacters.length > 0 ? (
                filteredCharacters.map((item) => (
                  <div
                    key={item.originalIndex}
                    className="lottie-item"
                    ref={(el) => (lottieRefs.current[item.originalIndex] = el)}
                    title={item.name}
                    onClick={() => handleAnimationClick(item.originalIndex)}
                  />
                ))
              ) : (
                <div className="no-uploaded-assets">
                  <div>
                    {searchTerm
                      ? `No characters match "${searchTerm}"`
                      : 'No characters available'}
                  </div>
                </div>
              )}
            </div>
          )}

        {selectedMenu === 'characters' && isAiSelected && (
          <div className="lottie-container">
            <p>Coming soon!</p>
          </div>
        )}

        {selectedMenu === 'characters' && isUploadedSelected && (
          <div className="uploaded-assets-grid">
            {filteredUploadedCharacters.length > 0 ? (
              filteredUploadedCharacters.map((asset, index) => (
                <div
                  key={`${asset.url}-${index}`}
                  className="uploaded-asset-item"
                  onClick={() => handleAssetClick(asset.url)}
                >
                  <img
                    src={asset.url}
                    alt={asset.filename || `Character ${index + 1}`}
                    title={asset.filename || `Character ${index + 1}`}
                    className="uploaded-asset-preview"
                  />
                </div>
              ))
            ) : (
              <div className="no-uploaded-assets">
                <div>
                  {searchTerm
                    ? `No uploaded characters match "${searchTerm}"`
                    : 'No uploaded characters'}
                </div>
              </div>
            )}
          </div>
        )}

        {selectedMenu === 'props' && !isAiSelected && !isUploadedSelected && (
          <div className="lottie-container" key="props-templated">
            {filteredProps.length > 0 ? (
              filteredProps.map((item) => (
                <div
                  key={item.originalIndex}
                  className="lottie-item"
                  ref={(el) =>
                    (lottiePropRefs.current[item.originalIndex] = el)
                  }
                  title={item.name}
                  onClick={() => handleAnimationClick(item.originalIndex)}
                />
              ))
            ) : (
              <div className="no-uploaded-assets">
                <div>
                  {searchTerm
                    ? `No props match "${searchTerm}"`
                    : 'No props available'}
                </div>
              </div>
            )}
          </div>
        )}

        {selectedMenu === 'props' && isAiSelected && (
          <div className="lottie-container">
            <p>Coming soon!</p>
          </div>
        )}

        {selectedMenu === 'props' && isUploadedSelected && (
          <div className="uploaded-assets-grid">
            {filteredUploadedProps.length > 0 ? (
              filteredUploadedProps.map((asset, index) => (
                <div
                  key={`${asset.url}-${index}`}
                  className="uploaded-asset-item"
                  onClick={() => handleAssetClick(asset.url)}
                >
                  <img
                    src={asset.url}
                    alt={asset.filename || `Prop ${index + 1}`}
                    title={asset.filename || `Prop ${index + 1}`}
                    className="uploaded-asset-preview"
                  />
                </div>
              ))
            ) : (
              <div className="no-uploaded-assets">
                <div>
                  {searchTerm
                    ? `No uploaded props match "${searchTerm}"`
                    : 'No uploaded props'}
                </div>
              </div>
            )}
          </div>
        )}

        {selectedMenu === 'audio' && !isAiSelected && !isUploadedSelected && (
          <ul className="menu-items">
            {filteredAudio.length > 0 ? (
              filteredAudio.map((item) => (
                <li
                  key={item.originalIndex}
                  className="menu-item"
                  onClick={() => onAudioSelect(item.src)}
                  onMouseEnter={() => handleAudioPlay(item.src)}
                  onMouseLeave={handleAudioStop}
                >
                  {item.label}
                </li>
              ))
            ) : (
              <li className="menu-item-empty">
                {searchTerm
                  ? `No audio matches "${searchTerm}"`
                  : `No items available for ${selectedMenu}`}
              </li>
            )}
          </ul>
        )}

        {selectedMenu === 'audio' && isAiSelected && (
          <div className="audio-container">
            <textarea
              className="audio-prompt-input"
              placeholder="Enter prompt, eg: Peaceful Piano Meditation"
              value={audioPrompt}
              onChange={(e) => setAudioPrompt(e.target.value)}
            />
            <input
              type="text"
              className="audio-title-input"
              placeholder="Enter title"
              value={audioPromptTitle}
              onChange={(e) => setAudioPromptTitle(e.target.value)}
            />
            <select
              id="audioPrompt"
              className="audio-prompt-select"
              value={audioPromptStyle}
              onChange={(e) => setAudioPromptStyle(e.target.value)}
              required
            >
              <option value="">Audio Style...</option>
              <option value="Jazz">Jazz</option>
              <option value="Classical">Classical</option>
              <option value="Electronic">Electronic</option>
            </select>
            {audioGate && (
              <CreditGateBanner
                gate={audioGate}
                resource="audio"
                onDismiss={() => setAudioGate(null)}
              />
            )}
            {generatedAudioUrl !== '' && (
              <>
                <div className="audio-prompt-heading">Generated Audio</div>
                <audio src={generatedAudioUrl} controls />
                <button
                  className="generate-audio-button"
                  onClick={() =>
                    onSaveAndCanvasImportAudio(
                      generatedAudioSourceUrl || generatedAudioUrl,
                      audioPromptTitle,
                      generatedAudioDuration
                    )
                  }
                >
                  Add to Timeline
                </button>
              </>
            )}
            <button
              className={
                generatedAudioUrl !== ''
                  ? 'generate-audio-button-secondary'
                  : 'generate-audio-button'
              }
              onClick={handleGenerateAudioClick}
            >
              {generatedAudioUrl !== '' ? 'Regenerate' : 'Generate Audio'}
            </button>
          </div>
        )}

        {selectedMenu === 'audio' && isUploadedSelected && (
          <>
            {filteredUploadedAudio.length > 0 ? (
              <ul className="menu-items">
                {filteredUploadedAudio.map((asset, index) => (
                  <li
                    key={`${asset.url}-${index}`}
                    className="menu-item"
                    onClick={() => onAudioSelect(asset.url, asset.filename)}
                    onMouseEnter={() => handleAudioPlay(asset.url)}
                    onMouseLeave={handleAudioStop}
                  >
                    {asset.filename ||
                      decodeURIComponent(
                        asset.url
                          .split('/')
                          .pop()
                          ?.split('?')[0]
                          ?.split('.')[0] || ''
                      )}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="no-uploaded-assets">
                <div>
                  {searchTerm
                    ? `No uploaded audio matches "${searchTerm}"`
                    : 'No uploaded audio files'}
                </div>
              </div>
            )}
          </>
        )}

        {selectedMenu === 'assets' && (
          <div className="assets-section">
            <div
              className={`upload-container ${isDragOver ? 'drag-over' : ''}`}
            >
              <input
                type="file"
                accept="audio/*,video/*"
                onChange={handleFileChange}
                multiple
                className="file-input"
                onDragEnter={handleUploadDragEnter}
                onDragOver={handleUploadDragOver}
                onDragLeave={handleUploadDragLeave}
                onDrop={handleUploadDrop}
              />
              <CloudUploadIcon
                className="upload-icon"
                sx={{ fontSize: 30, marginBottom: 1 }}
              />
              <span className="upload-heading">Upload Audio / Video</span>
              <span className="upload-subtitle">
                {isDragOver
                  ? 'Drop to upload'
                  : 'Drag files here or click to browse'}
              </span>
              <span className="upload-formats">
                MP4 · MOV · WEBM · AVI · MP3 · WAV · M4A · FLAC
              </span>
            </div>

            {isLoadingAssets && (
              <div className="assets-loader-container">
                <div className="assets-loader"></div>
                <p>Loading assets...</p>
              </div>
            )}

            {!isUploadedSelected && (
              <div className="assets-list">
                <div className="assets-section-heading">Your assets</div>
                {filteredAssets.length > 0 ? (
                  <div className="uploaded-assets-grid">
                    {filteredAssets.map((item) => {
                      const assetType = item.type;

                      return (
                        <div
                          key={item.url}
                          className="uploaded-asset-item"
                          onClick={() =>
                            handleAssetClick(item.url, assetType, item.label)
                          }
                        >
                          {assetType === 'image' || assetType === 'svg' ? (
                            <div className="image-asset-preview">
                              <AssetTypeIcon type="image" />
                              <div className="image-filename">{item.label}</div>
                            </div>
                          ) : assetType === 'audio' ? (
                            <div className="audio-asset-preview">
                              <AssetTypeIcon type="audio" />
                              <div className="audio-filename">{item.label}</div>
                            </div>
                          ) : assetType === 'video' ? (
                            <div className="video-asset-preview">
                              <AssetTypeIcon type="video" />
                              <div className="video-filename">{item.label}</div>
                            </div>
                          ) : assetType === 'json' ? (
                            <div className="json-asset-preview">
                              <AssetTypeIcon type="json" />
                              <div className="json-filename">{item.label}</div>
                            </div>
                          ) : (
                            <div className="unknown-asset-preview">
                              <AssetTypeIcon type="unknown" />
                              <div className="unknown-filename">
                                {item.label}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="no-uploaded-assets">
                    <FolderOffIcon
                      className="no-assets-icon"
                      sx={{ fontSize: 26, marginBottom: 1 }}
                    />
                    <div>
                      {searchTerm
                        ? `No assets match "${searchTerm}"`
                        : 'No uploaded assets for this video yet'}
                    </div>
                  </div>
                )}
              </div>
            )}

            {isUploadedSelected && (
              <div className="uploaded-assets-grid">
                {filteredUploadedAssets.length > 0 ? (
                  filteredUploadedAssets.map((asset, index) => (
                    <div
                      key={index}
                      className="uploaded-asset-item"
                      onClick={() =>
                        handleAssetClick(asset.url, asset.type, asset.filename)
                      }
                    >
                      {asset.type === 'image' || asset.type === 'svg' ? (
                        <div className="image-asset-preview">
                          <AssetTypeIcon type="image" />
                          <div className="image-filename">
                            {asset.url.split('/').pop()?.split('?')[0]}
                          </div>
                        </div>
                      ) : asset.type === 'audio' ? (
                        <div className="audio-asset-preview">
                          <AssetTypeIcon type="audio" />
                          <div className="audio-filename">
                            {asset.url.split('/').pop()?.split('?')[0]}
                          </div>
                        </div>
                      ) : asset.type === 'video' ? (
                        <div className="video-asset-preview">
                          <AssetTypeIcon type="video" />
                          <div className="video-filename">
                            {asset.url.split('/').pop()?.split('?')[0]}
                          </div>
                        </div>
                      ) : asset.type === 'json' ? (
                        <div className="json-asset-preview">
                          <AssetTypeIcon type="json" />
                          <div className="json-filename">
                            {asset.url.split('/').pop()?.split('?')[0]}
                          </div>
                        </div>
                      ) : (
                        <div className="unknown-asset-preview">
                          <AssetTypeIcon type="unknown" />
                          <div className="unknown-filename">
                            {asset.url.split('/').pop()?.split('?')[0]}
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="no-uploaded-assets">
                    <div>
                      {searchTerm
                        ? `No assets match "${searchTerm}"`
                        : 'No uploaded assets'}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {selectedMenu === 'backgrounds' &&
          !isAiSelected &&
          !isUploadedSelected && (
            <div className="background-container">
              {filteredBackgrounds.length > 0 ? (
                filteredBackgrounds.map((item) => (
                  <div className="image-container" key={item.originalIndex}>
                    <img
                      src={item.src}
                      alt={item.label}
                      title={item.label}
                      className="background-item"
                      onClick={() => handleBackgroundClick(item.originalIndex)}
                    />
                  </div>
                ))
              ) : (
                <div className="no-uploaded-assets">
                  <div>
                    {searchTerm
                      ? `No backgrounds match "${searchTerm}"`
                      : 'No backgrounds available'}
                  </div>
                </div>
              )}
            </div>
          )}

        {selectedMenu === 'backgrounds' && isAiSelected && (
          <div className="audio-container">
            <textarea
              className="audio-prompt-input"
              placeholder="Enter prompt, eg: Stary sky, green island"
              value={backgroundPrompt}
              onChange={(e) => setBackgroundPrompt(e.target.value)}
            />
            {backgroundGate && (
              <CreditGateBanner
                gate={backgroundGate}
                resource="background"
                onDismiss={() => setBackgroundGate(null)}
              />
            )}
            {generatedBackgroundImage !== '' && (
              <>
                <div className="audio-prompt-heading">Generated Art</div>
                <img
                  src={generatedBackgroundImage}
                  className="audio-generated-image"
                />
                <button
                  className="generate-audio-button"
                  onClick={() =>
                    onSaveAndCanvasImportBackground(generatedBackgroundImage)
                  }
                >
                  Save & Import to canvas
                </button>
              </>
            )}
            <button
              className={
                generatedBackgroundImage !== ''
                  ? 'generate-audio-button-secondary'
                  : 'generate-audio-button'
              }
              onClick={handleGenerateBackgroundClick}
            >
              {generatedBackgroundImage !== ''
                ? 'Regenerate'
                : 'Generate Background'}
            </button>
          </div>
        )}

        {selectedMenu === 'backgrounds' && isUploadedSelected && (
          <div className="background-container">
            {filteredUploadedBackgrounds.length > 0 ? (
              filteredUploadedBackgrounds.map((asset, index) => (
                <div className="image-container" key={`${asset.url}-${index}`}>
                  <img
                    src={asset.url}
                    alt={asset.filename || `Background ${index}`}
                    title={asset.filename || `Background ${index}`}
                    className="background-item"
                    onClick={() => onBackgroundSelect(asset.url)}
                  />
                </div>
              ))
            ) : (
              <div className="no-uploaded-assets">
                <div>
                  {searchTerm
                    ? `No uploaded backgrounds match "${searchTerm}"`
                    : 'No uploaded backgrounds'}
                </div>
              </div>
            )}
          </div>
        )}

        {selectedMenu === 'effects' && !isAiSelected && !isUploadedSelected && (
          <div className="background-container">
            {filteredEffects.length > 0 ? (
              filteredEffects.map((item) => (
                <div className="image-container" key={item.type}>
                  <img
                    src={item.image}
                    alt={`${item.type} effect`}
                    title={`${item.type} effect`}
                    className="background-item"
                    onClick={() => handleEffectMenuItemClick(item.type)}
                  />
                </div>
              ))
            ) : (
              <div className="no-uploaded-assets">
                <div>
                  {searchTerm
                    ? `No effects match "${searchTerm}"`
                    : 'No effects available'}
                </div>
              </div>
            )}
          </div>
        )}

        {selectedMenu === 'effects' && isAiSelected && (
          <div className="lottie-container">
            <p>Coming soon!</p>
          </div>
        )}

        {selectedMenu === 'effects' && isUploadedSelected && (
          <div className="background-container">
            {filteredUploadedEffects.length > 0 ? (
              filteredUploadedEffects.map((asset, index) => (
                <div className="image-container" key={`${asset.url}-${index}`}>
                  <img
                    src={asset.url}
                    alt={asset.filename || `Effect ${index}`}
                    title={asset.filename || `Effect ${index}`}
                    className="background-item"
                    onClick={() => handleAssetClick(asset.url)}
                  />
                </div>
              ))
            ) : (
              <div className="no-uploaded-assets">
                <div>
                  {searchTerm
                    ? `No uploaded effects match "${searchTerm}"`
                    : 'No uploaded effects'}
                </div>
              </div>
            )}
          </div>
        )}

        {selectedMenu === 'text' && (
          <ul className="text-menu-items">
            {!searchTerm.trim() && (
              <li
                className="text-menu-item text-template-card text-template-card-add"
                onClick={() => onTextSelect()}
              >
                <div className="text-template-top">
                  <img
                    src={TextIconSrc}
                    alt="Text Icon"
                    className="text-template-icon"
                  />
                  <span className="text-template-label">Add a text box</span>
                </div>
                <div className="text-template-subtitle">
                  Plain editable text
                </div>
              </li>
            )}
            {filteredTextTemplates.length > 0 ? (
              filteredTextTemplates.map((template) => (
                <li
                  key={template.label}
                  className="text-menu-item text-template-card"
                  onClick={() => onTextSelect(template)}
                >
                  <div className="text-template-top">
                    <span
                      className="text-template-swatch"
                      style={{
                        backgroundColor: template.fontProperties.fontColor,
                      }}
                    />
                    <span className="text-template-label">
                      {template.label}
                    </span>
                    {template.styleType === 'bubble' && (
                      <span className="text-template-tag">Bubble</span>
                    )}
                  </div>
                  <div
                    className="text-template-preview"
                    style={{
                      color: template.fontProperties.fontColor,
                      fontFamily: template.fontProperties.fontFamily,
                      fontWeight: template.fontProperties.fontWeight,
                      fontStyle: template.fontProperties.fontStyle,
                    }}
                  >
                    {template.text}
                  </div>
                </li>
              ))
            ) : (
              <li className="menu-item-empty">
                No text templates match &quot;{searchTerm}&quot;
              </li>
            )}
          </ul>
        )}

        {selectedMenu === 'layer' && (
          <DndProvider backend={HTML5Backend}>
            <LayerPanel
              layers={searchTerm.trim() ? filteredLayers : layers}
              handleLayerLock={(index) => {
                const target = searchTerm.trim()
                  ? (filteredLayers as any[])[index]
                  : undefined;
                handleLayerLock(
                  target && typeof target.originalIndex === 'number'
                    ? target.originalIndex
                    : index
                );
              }}
              onLayerDrag={(draggedIndex, droppedIndex) => {
                if (searchTerm.trim()) return;
                onLayerDrag(draggedIndex, droppedIndex);
              }}
            />
          </DndProvider>
        )}

        {selectedMenu === 'animations' && (
          <div className="tween-container">
            <div className="record-section">
              <h3 className="section-heading">Record your own animation</h3>
              <div className="record-controls">
                <div
                  className={`record-button ${isRecording ? 'recording' : ''}`}
                  onClick={() => handleTweeningOptionClick({ type: 'record' })}
                  title={isRecording ? 'Stop Recording' : 'Record Animation'}
                >
                  <div className="record-icon">⏺</div>
                </div>
                <div
                  className="play-button"
                  onClick={() =>
                    handleTweeningOptionClick({ type: 'playback' })
                  }
                  title="Play Animation"
                >
                  <div className="play-icon">▶</div>
                </div>
              </div>
            </div>
            <div className="template-section">
              <h3 className="section-heading">Template tweening options</h3>
              <div className="lottie-container">
                {filteredTweeningOptions.length > 0 ? (
                  filteredTweeningOptions.map((option: any, index: number) => (
                    <div
                      key={option.text || option.label || index}
                      className="uploaded-asset-item"
                      title={option.text || option.label || option.type}
                      onClick={() => handleTweeningOptionClick(option)}
                    >
                      {option.icon}
                    </div>
                  ))
                ) : (
                  <div className="no-uploaded-assets">
                    <div>No animations match &quot;{searchTerm}&quot;</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default Menu;
